import { useRef } from 'react'
import { useNavigate } from 'react-router'

import { ENTITY_LISTING_VIEW_TYPE } from '../../../../../constants/common'
import { useLinkLikeClick } from '../../../../../hooks'
import { isListViewTypeBoard } from '../../../../../utils/listViewType'
import { PageActions } from '../../../../Page'
import {
  getApplicantProfileLink,
  getMockApplicantsPolicy,
} from '../../../../RentalPortalApplicant'
import { Table, TableControlBar, TableControlButtons } from '../../../../Table'
import { TablePagination } from '../../../../TablePagination'
import {
  getApplicantViewRegimeOptions,
  getIsApplicantCompareViewType,
  RentalPortalApplicantBoard,
  useRentalPortalApplicantsViewRegime,
} from '../../../RentalPortalApplicants'
import { RentalPortalViewRegimeControl } from '../../../RentalPortalViewRegimeControl'
import {
  getHasApplicantComparisonCandidates,
  RentalPortalApplicantComparison,
} from '../RentalPortalApplicantComparison'
import { useListingProfileContext } from '../RentalPortalListingProfile.hooks'

import { ListingProfileApplicantsEmpty } from './ListingProfileApplicantsEmpty'
import { ListingProfileApplicantsLoadError } from './ListingProfileApplicantsLoadError'
import { ListingProfileApplicantsMatchRules } from './ListingProfileApplicantsMatchRules'
import { ListingProfileApplicantsRow } from './ListingProfileApplicantsRow'
import {
  LISTING_APPLICANT_COLUMNS_CONFIG,
  LISTING_PROFILE_APPLICANTS_SKELETON_ROWS,
  QA_NAME_LISTING_PROFILE_APPLICANTS_TABLE,
} from './RentalPortalListingProfileApplicants.constants'
import { useListingProfileApplicants } from './RentalPortalListingProfileApplicants.hooks'
import {
  getIsListingApplicantActionOpen,
  getIsListingApplicantsEmpty,
  getIsListingApplicantsError,
  getListingApplicantItemActions,
  getListingApplicantsState,
} from './RentalPortalListingProfileApplicants.utils'

import * as styles from './RentalPortalListingProfileApplicants.module.css'

function RentalPortalListingProfileApplicants() {
  const navigate = useNavigate()

  const { handleClick } = useLinkLikeClick()

  const { listingData } = useListingProfileContext()

  const containerRef = useRef(null)

  const {
    allItems,
    handleRefresh,
    handleSetPagination,
    hasError,
    hasStage,
    isInitiated,
    isLoading,
    items,
    pagination,
  } = useListingProfileApplicants({ listingId: listingData?.id })

  const isCompareAvailable = getHasApplicantComparisonCandidates(allItems)

  const { handleSetListViewType, listViewType } =
    useRentalPortalApplicantsViewRegime({
      hasStage,
      isCompareAvailable,
    })

  function handleShowMore() {
    handleSetListViewType(ENTITY_LISTING_VIEW_TYPE.table)
    handleSetPagination({ page: 1 })
  }

  function handleClickRow(id, event) {
    handleClick({ event, link: getApplicantProfileLink(id) })
  }

  function handleCallRowAction(action, item) {
    if (getIsListingApplicantActionOpen(action)) {
      navigate(getApplicantProfileLink(item.id))
    }
  }

  const state = getListingApplicantsState({
    applicants: items,
    hasError,
    isInitiated,
  })

  const isBoardRegime = isListViewTypeBoard(listViewType)

  const isCompareRegime = getIsApplicantCompareViewType(listViewType)

  const viewRegimeOptions = getApplicantViewRegimeOptions({
    hasStage,
    isCompareAvailable,
    listViewType,
  })

  const hasViewRegime =
    isInitiated &&
    viewRegimeOptions.length > 1 &&
    (!getIsListingApplicantsEmpty(state) || isBoardRegime || isCompareRegime)

  const { rental_erp_handoff_enabled: hasHandoff } = getMockApplicantsPolicy()

  // TODO: the rules section is admin-only (PM, 22.09) and rides the payload flag
  // until a matching permission exists in the section model.
  const matchRules = listingData?.match_rules?.can_configure_matching
    ? listingData.match_rules
    : null

  return (
    <div
      className={styles.RentalPortalListingProfileApplicants}
      ref={containerRef}
    >
      {hasViewRegime || matchRules ? (
        <TableControlBar>
          {matchRules ? (
            <ListingProfileApplicantsMatchRules
              listing={listingData}
              rules={matchRules}
              onReevaluated={handleRefresh}
            />
          ) : null}
          <PageActions className={styles.pageActions}>
            <TableControlButtons
              keepChangeViewTypeButtonOnNarrowScreen
              ChangeViewTypeButton={RentalPortalViewRegimeControl}
              ConfigButton={null}
              DensityButton={null}
              ResetSortingButton={null}
              changeViewTypeButtonProps={{
                hasBoard: hasStage,
                hasSplit: false,
                listViewType,
                options: viewRegimeOptions,
                onChange: handleSetListViewType,
              }}
              className={styles.controlButtons}
              isSkeleton={!isInitiated}
              refreshButtonProps={{
                isLoading,
                onClick: handleRefresh,
              }}
            />
          </PageActions>
        </TableControlBar>
      ) : null}
      {isCompareRegime ? (
        <RentalPortalApplicantComparison />
      ) : getIsListingApplicantsError(state) ? (
        <ListingProfileApplicantsLoadError
          isLoading={isLoading}
          onRetry={handleRefresh}
        />
      ) : isBoardRegime ? (
        <RentalPortalApplicantBoard
          isListingScoped
          emptyState={<ListingProfileApplicantsEmpty />}
          hasHandoff={hasHandoff}
          isLoading={!isInitiated}
          items={allItems}
          onClickCard={handleClickRow}
          onShowMore={handleShowMore}
        />
      ) : getIsListingApplicantsEmpty(state) ? (
        <ListingProfileApplicantsEmpty />
      ) : (
        <>
          <Table
            TableRow={ListingProfileApplicantsRow}
            actionsColumnWidth={0}
            columns={LISTING_APPLICANT_COLUMNS_CONFIG}
            containerRef={containerRef}
            context={QA_NAME_LISTING_PROFILE_APPLICANTS_TABLE}
            headerRowProps={{ resizable: false }}
            isLoading={isLoading}
            items={items}
            rowProps={{
              getActions: getListingApplicantItemActions,
              onCallAction: handleCallRowAction,
              onClick: handleClickRow,
            }}
            skeletonProps={{ withCheckbox: false }}
            skeletonRows={LISTING_PROFILE_APPLICANTS_SKELETON_ROWS}
          />
          <TablePagination
            responsive
            containerRef={containerRef}
            isSkeleton={!isInitiated}
            value={pagination}
            onChange={handleSetPagination}
          />
        </>
      )}
    </div>
  )
}

export default RentalPortalListingProfileApplicants
